import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaUserCircle,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { AxiosGetMethod, AxiosPutMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { setActivePage } from "../store/activePage";
import CitySearchSelect from "../components/city-search-select";

const getPasswordStrength = (password) => {
  const value = String(password || "");

  if (!value) {
    return {
      score: 0,
      label: "Add a new password",
      color: "bg-slate-200",
      textColor: "text-slate-400",
    };
  }

  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;

  if (score <= 1) {
    return {
      score,
      label: "Weak password",
      color: "bg-rose-500",
      textColor: "text-rose-500",
    };
  }

  if (score === 2 || score === 3) {
    return {
      score,
      label: "Medium password",
      color: "bg-amber-500",
      textColor: "text-amber-600",
    };
  }

  return {
    score,
    label: "Strong password",
    color: "bg-emerald-500",
    textColor: "text-emerald-600",
  };
};

const personalSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  occupation: Yup.string().required("Occupation is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  pincode: Yup.string()
    .matches(/^\d{6}$/, "Pincode must be 6 digits")
    .required("Pincode is required"),
});

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(6, "New password must be at least 6 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your new password"),
});

const inputClass =
  "w-full rounded-xl border bg-slate-50 px-4 py-2.5 text-xs font-bold text-slate-700 outline-none transition focus:bg-white focus:ring-2";

const readOnlyClass =
  "no-translate w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-xs font-bold text-slate-400 outline-none cursor-not-allowed";

const initialProfile = {
  first_name: "",
  last_name: "",
  occupation: "",
  mobile: "",
  email: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
};

const CustomerSettings = () => {
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(initialProfile);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const customerName = localStorage.getItem("customer-name") || "Customer";

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Settings",
        activeMenu: "settingsPage",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const loadProfile = async () => {
      setProfileLoading(true);
      const res = await AxiosGetMethod("customer/profile");
      if (res?.status === 200 && res?.data?.data) {
        const nextProfile = res.data.data.personal_info || res.data.data;
        setProfile({
          ...initialProfile,
          ...nextProfile,
        });
      } else {
        setProfile((prev) => ({
          ...prev,
          first_name: localStorage.getItem("customer-first-name") || prev.first_name,
          last_name: localStorage.getItem("customer-last-name") || prev.last_name,
          email: localStorage.getItem("customer-email") || prev.email,
          mobile: localStorage.getItem("customer-mobile") || prev.mobile,
        }));
      }
      setProfileLoading(false);
    };

    loadProfile();
  }, []);

  const personalFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      occupation: profile.occupation || "",
      email: profile.email || "",
      mobile: profile.mobile || localStorage.getItem("customer-mobile") || "",
      address: profile.address || "",
      city: profile.city || "",
      state: profile.state || "",
      pincode: profile.pincode || "",
    },
    validationSchema: personalSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      const { mobile, ...payload } = values;
      const res = await AxiosPutMethod("customer/profile", payload);
      setSubmitting(false);

      if (res?.status === 200 && res?.data?.data) {
        const nextProfile = res.data.data.personal_info || res.data.data;
        setProfile({
          ...initialProfile,
          ...nextProfile,
        });
        localStorage.setItem(
          "customer-name",
          `${nextProfile.first_name || values.first_name} ${nextProfile.last_name || values.last_name}`.trim(),
        );
        localStorage.setItem(
          "customer-email",
          nextProfile.email || values.email || "",
        );
        localStorage.setItem(
          "customer-mobile",
          nextProfile.mobile || values.mobile || "",
        );
        localStorage.setItem(
          "customer-first-name",
          nextProfile.first_name || values.first_name || "",
        );
        localStorage.setItem(
          "customer-last-name",
          nextProfile.last_name || values.last_name || "",
        );
        SuccessToast("Personal information updated successfully");
        return;
      }

      if (res?.status === 409) {
        setFieldError("email", "This email is already registered.");
        ErrorToast(res?.data?.message || "This email is already registered.");
        return;
      }

      ErrorToast(res?.data?.message || "Unable to update personal information");
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm, setSubmitting, setFieldError }) => {
      const res = await AxiosPutMethod("change/password", {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      });

      setSubmitting(false);

      if (res?.status === 200) {
        SuccessToast("Password updated successfully");
        resetForm();
        return;
      }

      if (res?.status === 400) {
        setFieldError("currentPassword", "Current password is incorrect");
        ErrorToast(res?.data?.message || "Unable to update password");
        return;
      }

      ErrorToast("Unable to update password right now");
    },
  });

  const passwordStrength = getPasswordStrength(passwordFormik.values.newPassword);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="space-y-6">
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 border border-amber-200/50 text-amber-600 shadow-sm">
              <FaLock className="text-base" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Security and Profile
              </p>
              <h2 className="mt-1 text-2xl font-bold text-slate-800">
                {customerName} Settings
              </h2>
              <p className="mt-1.5 max-w-xl text-xs text-slate-505 font-medium leading-relaxed">
                Update your personal details and password from one place.
                Mobile number is locked for safety.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 border border-slate-200/50 text-slate-600 shadow-sm">
              <FaUserCircle className="text-base" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">
                Personal Information
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 font-medium">
                Edit your profile, contact details, and address.
              </p>
            </div>
          </div>

          <form onSubmit={personalFormik.handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  First Name
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={personalFormik.values.first_name}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    personalFormik.touched.first_name &&
                    personalFormik.errors.first_name
                      ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                      : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                  }`}
                  placeholder="First name"
                />
                {personalFormik.touched.first_name &&
                  personalFormik.errors.first_name && (
                    <p className="mt-2 text-[10px] font-semibold text-rose-500">
                      {personalFormik.errors.first_name}
                    </p>
                  )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  Last Name
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={personalFormik.values.last_name}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    personalFormik.touched.last_name &&
                    personalFormik.errors.last_name
                      ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                      : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                  }`}
                  placeholder="Last name"
                />
                {personalFormik.touched.last_name &&
                  personalFormik.errors.last_name && (
                    <p className="mt-2 text-[10px] font-semibold text-rose-500">
                      {personalFormik.errors.last_name}
                    </p>
                  )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  Occupation
                </label>
                <input
                  type="text"
                  name="occupation"
                  value={personalFormik.values.occupation}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    personalFormik.touched.occupation &&
                    personalFormik.errors.occupation
                      ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                      : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                  }`}
                  placeholder="Occupation"
                />
                {personalFormik.touched.occupation &&
                  personalFormik.errors.occupation && (
                    <p className="mt-2 text-[10px] font-semibold text-rose-500">
                      {personalFormik.errors.occupation}
                    </p>
                  )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  Email
                </label>
                <input
                  name="email"
                  value={personalFormik.values.email}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`no-translate ${inputClass} ${
                    personalFormik.touched.email && personalFormik.errors.email
                      ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                      : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                  }`}
                  placeholder="Email address"
                />
                {personalFormik.touched.email && personalFormik.errors.email && (
                  <p className="mt-2 text-[10px] font-semibold text-rose-500">
                    {personalFormik.errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  Mobile Number
                </label>
                <input
                  value={personalFormik.values.mobile}
                  readOnly
                  className={readOnlyClass}
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-bold text-slate-700">
                Address
              </label>
              <textarea
                name="address"
                rows={3}
                value={personalFormik.values.address}
                onChange={personalFormik.handleChange}
                onBlur={personalFormik.handleBlur}
                data-force-tamil="true"
                lang="ta"
                className={`force-tamil ${inputClass} ${
                  personalFormik.touched.address && personalFormik.errors.address
                    ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                    : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                }`}
                placeholder="Address"
              />
              {personalFormik.touched.address && personalFormik.errors.address && (
                <p className="mt-2 text-[10px] font-semibold text-rose-500">
                  {personalFormik.errors.address}
                </p>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  City
                </label>
                <CitySearchSelect
                  value={personalFormik.values.city}
                  onChange={(city) => personalFormik.setFieldValue("city", city)}
                  onBlur={() => personalFormik.setFieldTouched("city", true)}
                  noAutoTranslate
                  placeholder="Search city"
                  className={`${inputClass} ${
                    personalFormik.touched.city && personalFormik.errors.city
                      ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                      : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                  }`}
                />
                {personalFormik.touched.city && personalFormik.errors.city && (
                  <p className="mt-2 text-[10px] font-semibold text-rose-500">
                    {personalFormik.errors.city}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={personalFormik.values.state}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`force-tamil ${inputClass} ${
                    personalFormik.touched.state && personalFormik.errors.state
                      ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                      : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                  }`}
                  placeholder="State"
                />
                {personalFormik.touched.state && personalFormik.errors.state && (
                  <p className="mt-2 text-[10px] font-semibold text-rose-500">
                    {personalFormik.errors.state}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">
                  Pincode
                </label>
                <input
                  name="pincode"
                  value={personalFormik.values.pincode}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className={`no-translate ${inputClass} ${
                    personalFormik.touched.pincode &&
                    personalFormik.errors.pincode
                      ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                      : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                  }`}
                  placeholder="6 digit pincode"
                />
                {personalFormik.touched.pincode &&
                  personalFormik.errors.pincode && (
                    <p className="mt-2 text-[10px] font-semibold text-rose-500">
                      {personalFormik.errors.pincode}
                    </p>
                  )}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={personalFormik.isSubmitting || profileLoading}
                className="rounded-xl bg-amber-600 px-6 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              >
                {personalFormik.isSubmitting ? "Updating..." : "Update Personal Info"}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 border border-amber-200/50 text-amber-600 shadow-sm">
              <FaCheckCircle className="text-base" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-850">
                Reset Password
              </h3>
              <p className="text-xs text-slate-450 mt-0.5 font-medium">
                Choose a strong password with at least 6 characters.
              </p>
            </div>
          </div>

          <form onSubmit={passwordFormik.handleSubmit} className="space-y-5">
            {[
              {
                id: "currentPassword",
                label: "Current Password",
                value: passwordFormik.values.currentPassword,
                visible: showCurrentPassword,
                setVisible: setShowCurrentPassword,
                autoComplete: "current-password",
              },
              {
                id: "newPassword",
                label: "New Password",
                value: passwordFormik.values.newPassword,
                visible: showNewPassword,
                setVisible: setShowNewPassword,
                autoComplete: "new-password",
              },
              {
                id: "confirmPassword",
                label: "Confirm New Password",
                value: passwordFormik.values.confirmPassword,
                visible: showConfirmPassword,
                setVisible: setShowConfirmPassword,
                autoComplete: "new-password",
              },
            ].map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={field.id}
                  className="mb-2 block text-xs font-bold text-slate-700"
                >
                  {field.label}
                </label>
                <div className="relative">
                  <input
                    id={field.id}
                    name={field.id}
                    type={field.visible ? "text" : "password"}
                    value={field.value}
                    onChange={passwordFormik.handleChange}
                    onBlur={passwordFormik.handleBlur}
                    className={`no-translate ${inputClass} pr-12 ${
                      passwordFormik.touched[field.id] &&
                      passwordFormik.errors[field.id]
                        ? "border-rose-300 focus:border-rose-450 focus:ring-rose-500/20"
                        : "border-slate-200 focus:border-amber-600 focus:ring-amber-500/20"
                    }`}
                    autoComplete={field.autoComplete}
                  />
                  <button
                    type="button"
                    onClick={() => field.setVisible((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                    aria-label={field.visible ? "Hide password" : "Show password"}
                  >
                    {field.visible ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {passwordFormik.touched[field.id] &&
                  passwordFormik.errors[field.id] && (
                    <p className="mt-2 text-[10px] font-semibold text-rose-500">
                      {passwordFormik.errors[field.id]}
                    </p>
                  )}
                {field.id === "newPassword" && (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`h-2 flex-1 rounded-full ${
                            passwordStrength.score >= level
                              ? passwordStrength.color
                              : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p className={`text-[10px] font-bold ${passwordStrength.textColor}`}>
                      {passwordStrength.label}
                    </p>
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={passwordFormik.isSubmitting}
                className="rounded-xl bg-amber-600 px-6 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              >
                {passwordFormik.isSubmitting ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default CustomerSettings;
